import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchFestivals, updateFirebaseSecret } from '../../../services/api';
import AdminLayout, { AdminBanner } from '../AdminLayout/AdminLayout';
import { useMessages } from '../../../i18n/I18n';
import './UpdateFirebaseSecret.css';
import { FestivalExcerpt, festivalName } from '../../../utils/festivals';

type Step = 'auth' | 'edit' | 'confirm' | 'done';

const UpdateFirebaseSecret: React.FC = () => {
  const history = useHistory();
  const t = useMessages('admin');
  const common = useMessages('common');

  const [step, setStep] = useState<Step>('auth');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [masterKey, setMasterKey] = useState('');
  const [festivals, setFestivals] = useState<FestivalExcerpt[]>([]);

  const [selectedFestivalId, setSelectedFestivalId] = useState('');
  const [firebaseSecretKey, setFirebaseSecretKey] = useState('');
  const [parsedSecret, setParsedSecret] = useState<object | null>(null);

  const [confirmName, setConfirmName] = useState('');

  const selectedFestival = festivals.find((f) => f.festival_id === selectedFestivalId) || null;
  const expectedConfirmation = selectedFestival ? festivalName(selectedFestival) : '';
  const isConfirmationValid =
    expectedConfirmation.length > 0 && confirmName.trim() === expectedConfirmation;

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const fetched = await fetchFestivals(masterKey);
      setFestivals(fetched);
      setStep('edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedFestivalId) {
      setError(t.noFestivalSelected);
      return;
    }
    if (!firebaseSecretKey) {
      setError(t.secretRequired);
      return;
    }

    let parsed: object;
    try {
      parsed = JSON.parse(firebaseSecretKey);
    } catch {
      setError(t.secretNotJson);
      return;
    }

    setParsedSecret(parsed);
    setConfirmName('');
    setStep('confirm');
  };

  const handleConfirmSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmationValid || !parsedSecret || !selectedFestivalId) {
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await updateFirebaseSecret(masterKey, selectedFestivalId, parsedSecret);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.error);
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToAdmin = () => history.push('/admin');

  return (
    <AdminLayout
      className="update-firebase-secret-page"
      title={t.tools.firebaseSecret.title}
      description={t.secretDescription}
      hasBackLink
    >
      <section className="admin-card">
        {error && <AdminBanner tone="danger">{error}</AdminBanner>}

        {step === 'auth' && (
          <form onSubmit={handleAuthSubmit}>
            <div className="admin-field">
              <label htmlFor="master-key">{t.masterKey}</label>
              <input
                type="password"
                id="master-key"
                value={masterKey}
                onChange={(e) => setMasterKey(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="admin-actions">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? t.loading : t.continue}
              </button>
            </div>
          </form>
        )}

        {step === 'edit' && (
          <form onSubmit={handleEditSubmit}>
            <div className="admin-field">
              <label htmlFor="festival-select">{t.festival}</label>
              <select
                id="festival-select"
                value={selectedFestivalId}
                onChange={(e) => setSelectedFestivalId(e.target.value)}
                required
              >
                <option value="" disabled>
                  {t.selectFestival}
                </option>
                {festivals.map((festival) => (
                  <option key={festival.festival_id} value={festival.festival_id}>
                    {festivalName(festival)} ({festival.festival_id})
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <label htmlFor="firebase-secret-key">{t.newSecretKey}</label>
              <textarea
                id="firebase-secret-key"
                value={firebaseSecretKey}
                onChange={(e) => setFirebaseSecretKey(e.target.value)}
                placeholder='{ "type": "service_account", ... }'
                required
              />
              <small className="admin-help">
                {t.secretHelp}
              </small>
            </div>
            <div className="admin-actions">
              <button type="submit" className="btn btn-primary">
                {t.review}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('auth');
                  setError(null);
                }}
                className="btn"
              >
                {common.back}
              </button>
            </div>
          </form>
        )}

        {step === 'confirm' && selectedFestival && (
          <form onSubmit={handleConfirmSubmit}>
            <AdminBanner tone="warning">
              {t.replaceWarningBefore}{' '}
              <strong>{festivalName(selectedFestival)}</strong>{t.replaceWarningAfter}
            </AdminBanner>
            <div className="admin-field">
              <label htmlFor="confirm-name">
                {t.typeToConfirmBefore}<strong>{festivalName(selectedFestival)}</strong>{t.typeToConfirmAfter}
              </label>
              <input
                type="text"
                id="confirm-name"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="admin-actions">
              <button
                type="submit"
                className="btn btn-danger"
                disabled={!isConfirmationValid || isLoading}
              >
                {isLoading ? t.updating : t.updateSecret}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('edit');
                  setError(null);
                }}
                className="btn"
                disabled={isLoading}
              >
                {common.back}
              </button>
            </div>
          </form>
        )}

        {step === 'done' && selectedFestival && (
          <div className="update-firebase-secret-done">
            <AdminBanner tone="success">
              {t.secretUpdatedBefore}{' '}
              <strong>{festivalName(selectedFestival)}</strong>{t.secretUpdatedAfter}
            </AdminBanner>
            <div className="admin-actions">
              <button type="button" onClick={goBackToAdmin} className="btn">
                {t.backToTools}
              </button>
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
};

export default UpdateFirebaseSecret;
